import React, { forwardRef, useEffect, useRef, useState } from "react";
import styles from "./LeumitDoctorResult.module.scss";
import { KeyValuePair, LeumitDoctorType } from "types";
//import { Camera, Female, Location, Male, Phone } from "assets/icons";
import SvgGenerator from "components/svgGenerator/SvgGenerator";
import Text from "components/text/Text";
import strings from "assets/strings";
import { getAllClassNames, setParams, formatDate, getDayOfWeek } from "helpers";
import Button from "components/buttons/button/Button";
import { APPOINTMENT_URL, grad1, grad2 } from "assets/constants";
import mergedSettings from "assets/mergeSettings";
import { Doctorim, Shidurit } from "assets/images";
import Popup from "components/popup/Popup";
import { CustomalertPopup } from "components/popup/customAlertPopup/CustomalertPopup";
import settings from "assets/mergeSettings";
import PhoneLink from "components/phoneLink/PhoneLink";

interface LeumitDoctorResultProps {
  hit: LeumitDoctorType;
  tabIndex?: number;
}

const getSearchApiUrl = (): string => {
  try {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes("infodev")) {
      return "infodevsearchapi.leumit.co.il";
    }
    if (hostname.includes("infoqa")) {
      return "infoqasearchapi.leumit.co.il";
    }
    return "infosearchapi.leumit.co.il";
  } catch {
    return "infosearchapi.leumit.co.il";
  }
};

const SEARCH_API_URL = getSearchApiUrl();

const formatShiduritFirstSlot = (slot: string): string => {
  const trimmedSlot = slot?.trim();
  if (!trimmedSlot) {
    return "";
  }

  const slotMatch = trimmedSlot.match(/^(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2})(?::\d{2})?$/);
  if (!slotMatch) {
    return trimmedSlot;
  }

  const [, datePart, timePart] = slotMatch;
  return `${datePart} בשעה ${timePart}`;
};

const LeumitDoctorResult = forwardRef<HTMLDivElement, LeumitDoctorResultProps>(
  ({ hit, tabIndex }, ref) => {

  const popupRef = useRef<HTMLDivElement>(null);
  const [shiduritFirstSlot, setShiduritFirstSlot] = useState<string>("");

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991.98);
  const handleResize = () => {
    setIsMobile(window.innerWidth < 992);
  };

  useEffect(() => {
    // Set up the event listener
    window.addEventListener('resize', handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleToggleVisibility = () => {
    if (popupRef.current) {
      popupRef.current.classList.toggle('show');
    }
  };

  const onNavigate = (hit: LeumitDoctorType) => {
    const { medicalCenter } = hit;
    const { address } = medicalCenter;
    const encodedAddress = encodeURIComponent(address);
    const mobileUrl = `geo://0,0?q=${encodedAddress}`;
    const iosUrl = `maps://?q=${encodedAddress}`;
    const desktopUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;

    function isIOS() {
      return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    }
    const url = isMobile ? (isIOS() ? iosUrl : mobileUrl) : desktopUrl;
    window.open(url, "_blank");
  };

  const onAppointment = (hit: LeumitDoctorType) => {
    const data: KeyValuePair[] = [
      { key: "DoctorLicenseNumber", value: hit.licenseId },
      { key: "DoctorSpecilization", value: hit.doctorSpecilizationCode },
      { key: "region", value: hit.medicalCenter.regionCode },
      { key: "BranchCode", value: `${hit.medicalCenter.branchNum}${hit.medicalCenter.subBranchNum}` },
      { key: "SubBranchNum", value: hit.medicalCenter.subBranchNum || '' },
      { key: "BranchNum", value: hit.medicalCenter.branchNum || '' },
      { key: "Place", value: hit.place || '' },
      { key: "title", value: hit.title || '' },
      { key: "Address", value: hit.medicalCenter.address || '' },
      { key: "Name", value: hit.fullName || '' },
      { key: "MedicalFieldName", value: hit.medicalFieldName || '' },
      { key: "acceptVirtual", value: `${hit.telemdicineToHome || ''}` },
      { key: "PhoneVirtualSite", value: `${hit.medicalCenter.workHours?.phoneAppointment || ''}` },
      //{ key: "DoctorSpecilizationCode", value: `${hit.DoctorSpecilizationCode}` },
      { key: "PhoneVirtualSite", value: `${hit.phoneAppointment || ''}` },
      { key: "DoctorSpecilization", value: `${hit.specializationCode || ''}` },
      { key: "HasVirtualSite", value: `${hit.virtualSite || ''}` },

    ];
    const url = `${APPOINTMENT_URL}&${setParams(data)}`;


    window.open(url, "_blank");
  };

  const { firstName, lastName, title, medicalFieldName, firstAvailableAppointmentDate, externalUrl } = hit;
  const isShidurit = hit.externalSource === "shidurit";

  //const [showAppointmentPopup, setShowAppointmentPopup] = useState(false);

  useEffect(() => {
    const fetchShiduritFirstSlot = async () => {
      if (!isShidurit || !hit.licenseId) {
        setShiduritFirstSlot("");
        return;
      }

try {
  const response = await fetch(
    `https://${SEARCH_API_URL}/api/search/shidurit/doctors/${hit.licenseId}/firstslot/auto-token`
  );

  if (!response.ok) {
          setShiduritFirstSlot("");
          return;
        }

        const data = await response.json();
        setShiduritFirstSlot(typeof data?.Slot === "string" ? data.Slot : "");
      } catch {
        setShiduritFirstSlot("");
      }
    };

    fetchShiduritFirstSlot();
  }, [isShidurit, hit.licenseId]);

  const defaultFirstSlotText = `${getDayOfWeek(String(firstAvailableAppointmentDate))} ${formatDate(String(firstAvailableAppointmentDate))}`;
  const rawFirstSlotText = isShidurit ? formatShiduritFirstSlot(shiduritFirstSlot) : defaultFirstSlotText;
  const firstSlotText = rawFirstSlotText?.trim() || "לא זמין כרגע";

  const textLine1Desktop = `בחרת לזמן תור ל${title} ${firstName} ${lastName} בתחום ${medicalFieldName}`;

  const textLine1Desktop2 = `התור הזמין הקרוב ביותר: ${firstSlotText}`;

  const textLine1Mobile = `בחרת לזמן תור ל${title} ${firstName} ${lastName} בתחום ${medicalFieldName}`;

  const textLine1Mobile2 = `התור הזמין הקרוב ביותר: <div>${firstSlotText}</div>`;


  let tooltipContentFirst = strings.doctorSearchResults.doctorimPopupContent;
  let tooltipContentSecond = '';

  if (strings.doctorSearchResults.doctorimPopupContent.length > 200) {
    tooltipContentFirst = strings.doctorSearchResults.doctorimPopupContent.substring(0, 200) + '...';
    tooltipContentSecond = strings.doctorSearchResults.doctorimPopupContent.substring(200);
  }

  const onExternalAppointment = (hit: LeumitDoctorType) => {    
    window.open(hit.externalUrl, "_blank");
    //setShowAppointmentPopup(true);
  }


  const geoDistance = hit._rankingInfo?.geoDistance;

const shouldShowDistance =
  typeof geoDistance === "number" && Number.isFinite(geoDistance);

const distanceText = shouldShowDistance
  ? `${(geoDistance / 1000).toFixed(2)}\u00A0ק"מ`
  : "";

  console.log("geoDistance", hit.objectID, hit._rankingInfo?.geoDistance, typeof hit._rankingInfo?.geoDistance);


  const hasPhones =
  !!hit.medicalCenter.phone1 || !!hit.medicalCenter.phone2;

  const appointmentSystemLogo = isShidurit ? Shidurit : Doctorim;
  const appointmentSystemAlt = isShidurit ? "שידורית" : "דוקתורים";


  return (
    <div className={styles.hitDoctor} ref={ref} tabIndex={tabIndex}>
   <div className={styles.distanceSection}>
  {shouldShowDistance && (
    <span dir="ltr" style={{ unicodeBidi: "plaintext" }}>
      <Text className={styles.textTitle2} text={distanceText} />
    </span>
  )}
</div>


  
      <div className={styles.doctorDetails}>
        <div className={styles.section2}>
          {hit.gender === "נ" ? (
            <SvgGenerator name="Female" className={styles.avatar} objectName="doctorSearchResults" title={'רופאה'} />
          ) : (
            //<Male className={styles.avatar} />
            <SvgGenerator name="Male" className={styles.avatar} objectName="doctorSearchResults" title={'רופא'} />
          )}
          <div className={styles.divText} >

            {hit.objectType==="Doctor" && hit.doctorType === 1 && (
           <Text
              className={styles.textGray}
              text={hit.gender === "נ" ? strings.doctorSearchResults.sergeonFemale : strings.doctorSearchResults.sergeonMale}
            />

            )
          }
            <a
              id={`onlineOrder_${hit.objectID}`}
              href={`${hit.objectType == 'Doctor'
                ? mergedSettings.doctorPage
                : (hit.objectType == 'Diet'
                  ? mergedSettings.dietPage
                  : mergedSettings.socialWorkerPage)
                }${hit.licenseId ? `?licenseId=${hit.licenseId}` : ''
                }${hit.medicalFieldFieldCode ? `${hit.licenseId ? '&' : '?'}code=${hit.medicalFieldFieldCode}` : ''
                }${hit.doctorType ? `${hit.licenseId || hit.medicalFieldFieldCode ? '&' : '?'}doctorType=${hit.doctorType}` : ''
                }${hit.place ? `${hit.licenseId || hit.medicalFieldFieldCode || hit.doctorType ? '&' : '?'}place=${hit.place}` : ''
                }`}
              className={styles.textTitle}
            >
              {`${hit.title} ${hit.firstName} ${hit.lastName}`}
            </a>
            <Text className={styles.textPurple} text={`${hit.medicalFieldName ? hit.medicalFieldName : ''} ${hit.subMedicalFieldNames ? ` | ${hit.subMedicalFieldNames.join(",")}` : ''} `} />
          </div>
        </div>

        {hit.medicalCenter.city && <div className={styles.section}>
          <div className={styles.divText}>
            <Text className={styles.textTitle2} text={hit.medicalCenter.name} />
            <span
              id={`navigateToAddress_${hit.objectID}`}
              className={styles.textAddress}>{`${hit.medicalCenter.city}${hit.medicalCenter.street ? ',' : ''} ${hit.medicalCenter.street} ${hit.medicalCenter.houseNumber}`}
            </span>
            {hit._geoloc && <span onClick={() => onNavigate(hit)} aria-describedby={`navigateToAddress_${hit.objectID}`} className={styles.spanClick} tabIndex={0} role="link">
              {/* <Location className={styles.icon} /> */}
              <SvgGenerator name="Location" className={styles.icon} objectName="doctorSearchResults" />
              <Text
                className={styles.textNavigate}
                text={strings.doctorSearchResults.navigateToAddress}
              />
            </span>
            }
          </div>
        </div>
        }
        <div className={styles.section}>
          <div className={styles.divText}>
            {(hit.phoneAppointment || hit.telemdicineToHome) &&
              <Text
                className={styles.textTitle2}
                text={strings.doctorSearchResults.moreInfo}
              />
            }
            {hit.phoneAppointment && (
              <span className={getAllClassNames([styles.span, styles.marginTop])}>
                {/* <Phone className={styles.icon} /> */}
                <SvgGenerator name="Phone" className={styles.icon} objectName="doctorSearchResults" />
                <Text text={strings.doctorSearchResults.phoneAppointment} />
              </span>
            )}
            {hit.telemdicineToHome && (
              <span className={getAllClassNames([styles.span, styles.marginTop])}>
                {/* <Camera className={styles.icon} /> */}
                <SvgGenerator name="Camera" className={styles.icon} objectName="doctorSearchResults" />
                <Text text={strings.doctorSearchResults.videoAppointment} />
              </span>
            )}
          </div>
        </div>
        <div className={styles.section}>
          {(externalUrl && externalUrl !== '') ? (
            <>
              <Button
                className={styles.OrderButton}
                aria-describedby={`onlineOrder_${hit.objectID}`}
                color1={grad1}
                color2={grad2}
                onClick={() => handleToggleVisibility()}
                text={strings.doctorSearchResults.getAppointment}
              />
              <span 
                className={getAllClassNames([
                  styles.span,
                  styles.marginTop,
                  styles.appointmentSystemRow,
                  styles.appointmentSystemUnderButton
                  ])}>
                <Text className={styles.textDoctorim} text={strings.doctorSearchResults.appointmentSystem} />
                <img src={appointmentSystemLogo} alt={appointmentSystemAlt} className={styles.appointmentSystemLogo}/>
              </span>
            </>
          ) : (hit.onlineOrder) ? (
            <Button
              className={styles.OrderButton}
              aria-describedby={`onlineOrder_${hit.objectID}`}
              color1={grad1}
              color2={grad2}
              onClick={() => onAppointment(hit)}
              text={strings.doctorSearchResults.getAppointment}
            />
          ) : (!hit.onlineOrder && hit.medicalCenter.inBranch) ?
            (
              <>
                <Text text={strings.doctorSearchResults.callToMoked} style={{ fontSize: 16 }} />
                <PhoneLink phoneNumber={String(settings.mokedPhone)} />
              </>
            ) : (!hit.onlineOrder && !hit.medicalCenter.inBranch) ?
            (
              hasPhones ? (
                <>
                  <Text
                    text={strings.doctorSearchResults.callToOrder}
                    style={{ fontSize: 16 }}
                  />
            
                  {hit.medicalCenter.phone1 && (
                    <PhoneLink phoneNumber={String(hit.medicalCenter.phone1)} />
                  )}
            
                  {hit.medicalCenter.phone2 && (
                    <PhoneLink phoneNumber={String(hit.medicalCenter.phone2)} />
                  )}
                </>
              ) :null
              //  (
              //   <Text
              //     text={strings.doctorSearchResults.medicalCenterText}
              //     style={{ fontSize: 16 }}
              //   />
              // )
            )
            
              : null
          }
        </div>

        {<Popup
          ref={popupRef}
          style={{ padding: '25px', height: 'auto' }}
          height={'auto'}
          width={690}
          onClose={() => handleToggleVisibility()}>
          <CustomalertPopup
            title={isShidurit ? "יש לזמן תור דרך מערכת Rofim.Org" : strings.doctorSearchResults.doctorimPopupTitle}
            titeStyle={{ padding: '7px 0 17px' }}
            buttonText={strings.doctorSearchResults.getAppointment}
            onClose={() => onExternalAppointment(hit)}
            showButton
            paragraphs={
              [
                { text: `${isMobile ? textLine1Mobile : textLine1Desktop}`, style: { background: '#F6F1F6', whiteSpace: 'pre-line', padding: '1rem', width:'100%' }, isHtml: isMobile ? true : false },
                { text: isMobile ? textLine1Mobile2 : textLine1Desktop2, style: { background: '#F6F1F6', whiteSpace: 'pre-line', padding: '0 1rem 1rem', width:'100%' }, isHtml: isMobile ? true : false },
                { text: tooltipContentFirst, style: { whiteSpace: 'pre-line', lineHeight: 1.5 } },
                //{ text: textLine4, style: { color: '#004A80', fontSize: 16, fontWeight: 'bold', marginTop: 28 } }
              ]
            }
            toggledTextExpand={strings.globalStrings.expand}
            toggledTextCollapse={strings.globalStrings.collapse}
            toggledTextTitleStyle={{ color: '#2E567A', fontSize: 16 }}
            toggledText={tooltipContentSecond}
          />
        </Popup>}
      </div>
    </div>
  );
});

export default LeumitDoctorResult;
